import { useEffect, useState } from "react";
import { Pin, PinOff, Trash2, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, timeAgo } from "@/lib/api";
import { SectionTitle, Field, Pill, EmptyState } from "@/components/Bits";

const input = "w-full rounded-sm border border-line bg-white px-3 py-2 text-sm outline-none focus:border-lime";

export default function InternalNotes({ projectId, spaces = [], selections = [], vendors = [], compact }) {
  const [notes, setNotes] = useState([]);
  const [body, setBody] = useState("");
  const [links, setLinks] = useState({ space_id: "", product_id: "", vendor_id: "" });
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState("");

  const load = () =>
    api.get(`/projects/${projectId}/notes`).then((r) => setNotes(r.data)).catch(() => setNotes([]));

  useEffect(() => {
    if (projectId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const add = async () => {
    if (!body.trim()) return;
    try {
      await api.post(`/projects/${projectId}/notes`, {
        body,
        space_id: links.space_id || null,
        product_id: links.product_id || null,
        vendor_id: links.vendor_id || null,
      });
      setBody("");
      setLinks({ space_id: "", product_id: "", vendor_id: "" });
      toast.success("Note added");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const act = async (fn) => {
    try {
      await fn();
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <section data-testid="internal-notes">
      <SectionTitle>Internal Notes</SectionTitle>
      <p className="-mt-4 mb-6 text-xs text-fog">Visible to your team only. Clients never see these.</p>

      <div className="card-surface p-5">
        <textarea
          data-testid="internal-note-input"
          rows={compact ? 2 : 3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="e.g. Installer confirmed access through the rear loading entrance."
          className={input}
        />
        {!compact && (
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <Field label="Room (optional)">
              <select data-testid="note-space-select" value={links.space_id} onChange={(e) => setLinks({ ...links, space_id: e.target.value })} className={input}>
                <option value="">—</option>
                {spaces.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
            <Field label="Selection (optional)">
              <select data-testid="note-product-select" value={links.product_id} onChange={(e) => setLinks({ ...links, product_id: e.target.value })} className={input}>
                <option value="">—</option>
                {selections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
            <Field label="Vendor (optional)">
              <select data-testid="note-vendor-select" value={links.vendor_id} onChange={(e) => setLinks({ ...links, vendor_id: e.target.value })} className={input}>
                <option value="">—</option>
                {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </Field>
          </div>
        )}
        <button data-testid="internal-note-submit" onClick={add} className="mt-4 rounded-sm bg-ink px-4 py-2 text-sm text-white hover:bg-stone">
          Add note
        </button>
      </div>

      {notes.length === 0 ? (
        <div className="mt-6">
          <EmptyState testId="internal-notes-empty" title="No internal notes yet" body="Jot down anything the team needs to remember." />
        </div>
      ) : (
        <div className="mt-6 space-y-3" data-testid="internal-notes-list">
          {notes.map((n) => (
            <div key={n.id} className={`card-surface p-5 ${n.pinned ? "border-lime bg-[#FCFCF7]" : ""}`} data-testid={`internal-note-${n.id}`}>
              {editingId === n.id ? (
                <div className="space-y-3">
                  <textarea data-testid={`note-edit-input-${n.id}`} rows={3} value={draft} onChange={(e) => setDraft(e.target.value)} className={input} />
                  <div className="flex gap-2">
                    <button data-testid={`note-edit-save-${n.id}`} onClick={() => act(async () => { await api.patch(`/notes/${n.id}`, { body: draft }); setEditingId(null); })} className="inline-flex items-center gap-2 rounded-sm bg-lime px-3 py-1.5 text-xs text-ink">
                      <Check className="h-3.5 w-3.5" strokeWidth={1.5} /> Save
                    </button>
                    <button onClick={() => setEditingId(null)} className="inline-flex items-center gap-2 rounded-sm border border-line px-3 py-1.5 text-xs text-stone">
                      <X className="h-3.5 w-3.5" strokeWidth={1.5} /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <p className="max-w-2xl text-sm leading-relaxed text-ink">
                      {n.pinned && <Pin className="mr-2 inline h-3.5 w-3.5 text-lime-dark" strokeWidth={1.5} />}
                      {n.body}
                    </p>
                    <div className="flex shrink-0 gap-2">
                      <button data-testid={`note-pin-${n.id}`} onClick={() => act(() => api.post(`/notes/${n.id}/pin`))} className="rounded-sm border border-line p-1.5 text-stone hover:border-lime hover:text-lime-dark" aria-label="Pin note">
                        {n.pinned ? <PinOff className="h-3.5 w-3.5" strokeWidth={1.5} /> : <Pin className="h-3.5 w-3.5" strokeWidth={1.5} />}
                      </button>
                      <button data-testid={`note-edit-${n.id}`} onClick={() => { setEditingId(n.id); setDraft(n.body); }} className="rounded-sm border border-line p-1.5 text-stone hover:border-lime" aria-label="Edit note">
                        <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </button>
                      <button data-testid={`note-delete-${n.id}`} onClick={() => act(() => api.delete(`/notes/${n.id}`))} className="rounded-sm border border-line p-1.5 text-stone hover:border-clay hover:text-clay" aria-label="Delete note">
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-fog">
                    <span>{n.author_name}</span>
                    <span className="num">{timeAgo(n.created_at)}</span>
                    {n.updated_at && <span className="num">edited {timeAgo(n.updated_at)}</span>}
                    {n.space_name && <Pill tone="bg-line text-stone">{n.space_name}</Pill>}
                    {n.product_name && <Pill tone="bg-line text-stone">{n.product_name}</Pill>}
                    {n.vendor_name && <Pill tone="bg-line text-stone">{n.vendor_name}</Pill>}
                    {n.decision_title && <Pill tone="bg-line text-stone">{n.decision_title}</Pill>}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
